// Caminho relativo e direto no model, não a porta do kernel: o BFF importa
// este arquivo (server/routes/billing.ts) e lá não há alias `@/` nem DOM.
import { CHANNEL_COPY, type AccountCapacity, type QuotaChannel } from '../../marketing-shared/model/types';

export type { QuotaChannel };

/** Mesmos números da demonstração de e-mail. A cota passa a ser por passbook. */
export const DEFAULT_EMAIL_QUOTA: AccountCapacity = {
  planName: 'Starter',
  cycleLimit: 50_000,
  sent: 28_939,
  available: 21_061,
  renewsOn: '2026-11-10T12:00:00-03:00',
  transactionalReserve: 4_000,
  companyLimit: 12_000,
  campaignLimit: 8_000,
};

/** Mesmos números da demonstração de SMS, separados do e-mail. */
export const DEFAULT_SMS_QUOTA: AccountCapacity = {
  planName: 'Starter',
  cycleLimit: 100,
  sent: 28,
  available: 72,
  renewsOn: '2026-11-10T12:00:00-03:00',
  transactionalReserve: 0,
  companyLimit: 80,
  campaignLimit: 60,
};

export interface PassbookQuotas {
  passbookId: string;
  email: AccountCapacity;
  sms: AccountCapacity;
}

export type PixChargeStatus = 'pending' | 'paid' | 'expired' | 'failed';

export interface PixCharge {
  id: string;
  passbookId: string;
  channel: QuotaChannel;
  slots: number;
  amountCents: number;
  status: PixChargeStatus;
  provider: 'bling';
  externalId?: string;
  copiaECola?: string;
  qrCodeBase64?: string;
  message: string;
  createdAt: string;
}

export function seedQuotas(passbookId: string): PassbookQuotas {
  return {
    passbookId,
    email: { ...DEFAULT_EMAIL_QUOTA },
    sms: { ...DEFAULT_SMS_QUOTA },
  };
}

export function reserveSlots(
  quotas: PassbookQuotas,
  channel: QuotaChannel,
  slots: number,
): { ok: true; quotas: PassbookQuotas } | { ok: false; message: string } {
  const amount = Math.floor(slots);
  if (!Number.isFinite(amount) || amount <= 0) return { ok: true, quotas };
  const current = quotas[channel];
  if (amount > current.available) {
    const { label } = CHANNEL_COPY[channel];
    return { ok: false, message: `A cota de ${label} tem ${current.available} slots. Esta campanha pede ${amount}.` };
  }
  return {
    ok: true,
    quotas: {
      ...quotas,
      [channel]: {
        ...current,
        available: current.available - amount,
        sent: current.sent + amount,
      },
    },
  };
}

/** Recarga avulsa: aumenta o disponível e o teto do ciclo. Não mexe no que já foi enviado. */
export function creditSlots(quotas: PassbookQuotas, channel: QuotaChannel, slots: number): PassbookQuotas {
  const amount = Math.floor(slots);
  if (!Number.isFinite(amount) || amount <= 0) return quotas;
  const current = quotas[channel];
  return {
    ...quotas,
    [channel]: {
      ...current,
      available: current.available + amount,
      cycleLimit: current.cycleLimit + amount,
    },
  };
}

const PAID_WORDS = new Set(['pago', 'paid', 'recebido', 'liquidado', 'quitado', 'received']);

/** Só trata como pago um status explícito. Número solto da API não credita. */
export function blingPayloadIsPaid(payload: unknown): boolean {
  const seen = new Set<unknown>();
  const visit = (value: unknown, depth: number): boolean => {
    if (depth > 4 || value == null || seen.has(value)) return false;
    if (typeof value === 'string') return PAID_WORDS.has(value.trim().toLowerCase());
    if (typeof value !== 'object') return false;
    seen.add(value);
    for (const entry of Object.values(value as Record<string, unknown>)) {
      if (visit(entry, depth + 1)) return true;
    }
    return false;
  };
  return visit(payload, 0);
}

export function readPixFields(payload: unknown): { copiaECola?: string; qrCodeBase64?: string; externalId?: string } {
  if (!payload || typeof payload !== 'object') return {};
  const root = payload as Record<string, unknown>;
  const data = root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const pix = data.pix && typeof data.pix === 'object' ? (data.pix as Record<string, unknown>) : data;
  const text = (...keys: string[]) => {
    for (const key of keys) {
      const value = pix[key] ?? data[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return undefined;
  };
  const id = data.id ?? root.id;
  return {
    copiaECola: text('copiaECola', 'pixCopiaECola', 'codigoPix', 'emv', 'brcode'),
    qrCodeBase64: text('qrCodeBase64', 'qrcodeBase64', 'qrCode', 'imagemQrcode'),
    externalId: id != null && String(id).trim() ? String(id) : undefined,
  };
}

export interface QuotaBook {
  get(passbookId: string): PassbookQuotas;
  replace(next: PassbookQuotas): void;
  reserve(passbookId: string, channel: QuotaChannel, slots: number): { ok: true; quotas: PassbookQuotas } | { ok: false; message: string };
  credit(passbookId: string, channel: QuotaChannel, slots: number): PassbookQuotas;
  saveCharge(charge: PixCharge): void;
  getCharge(id: string): PixCharge | undefined;
}

export function createQuotaBook(): QuotaBook {
  const quotas = new Map<string, PassbookQuotas>();
  const charges = new Map<string, PixCharge>();

  const get = (passbookId: string) => {
    const existing = quotas.get(passbookId);
    if (existing) return existing;
    const seeded = seedQuotas(passbookId);
    quotas.set(passbookId, seeded);
    return seeded;
  };

  return {
    get,
    replace(next) {
      quotas.set(next.passbookId, next);
    },
    reserve(passbookId, channel, slots) {
      const result = reserveSlots(get(passbookId), channel, slots);
      if (result.ok) quotas.set(passbookId, result.quotas);
      return result;
    },
    credit(passbookId, channel, slots) {
      const next = creditSlots(get(passbookId), channel, slots);
      quotas.set(passbookId, next);
      return next;
    },
    saveCharge(charge) {
      charges.set(charge.id, charge);
    },
    getCharge(id) {
      return charges.get(id);
    },
  };
}

/** Credita uma vez. Status que não é `paid` não altera a cota. */
export function settleCharge(
  book: QuotaBook,
  chargeId: string,
  status: PixChargeStatus,
): { charge: PixCharge | null; credited: boolean } {
  const charge = book.getCharge(chargeId);
  if (!charge) return { charge: null, credited: false };
  if (charge.status === 'paid') return { charge, credited: false };
  if (status !== 'paid') {
    const next = { ...charge, status, message: status === 'expired' ? 'A cobrança expirou sem pagamento.' : charge.message };
    book.saveCharge(next);
    return { charge: next, credited: false };
  }
  book.credit(charge.passbookId, charge.channel, charge.slots);
  const next = { ...charge, status: 'paid' as const, message: 'Pagamento confirmado. A cota foi creditada.' };
  book.saveCharge(next);
  return { charge: next, credited: true };
}
